// frontend/src/utils/domain-utils.js

/**
 * Brand configuration for MaxResumes.Com
 */
const BRAND_CONFIG = {
  name: 'MaxResumes.Com',
  logo: '/assets/logos/maxresumes-logo.png',
  primaryColor: '#2563eb',
  secondaryColor: '#1e40af',
  tagline: 'Build professional resumes in minutes',
  description: 'Create beautiful, ATS-friendly resumes that get you hired',
  projectId: 'maxresumes',
  siteName: 'maxresumes'
};

/**
 * Price configuration for all plans (in different currencies)
 * Unified pricing across both domains
 */
const PRICE_CONFIG = {
  daily: {
    inr: {
      amount: 5900,
      formatted: '₹59',
      period: 'Per day',
      billingType: 'payment'
    },
    usd: {
      amount: 100,
      formatted: '$1.00',
      period: 'Per day',
      billingType: 'recurring'
    }
  },
  sevenDays: {
    inr: {
      amount: 69000,
      formatted: '₹690',
      period: '7 days access',
      billingType: 'payment'
    },
    usd: {
      amount: 900,
      formatted: '$9.00',
      period: '7 days access',
      billingType: 'payment'
    }
  },
  oneMonth: {
    inr: {
      amount: 98000,
      formatted: '₹980',
      period: 'One-time payment',
      billingType: 'payment'
    },
    usd: {
      amount: 1500,
      formatted: '$15.00',
      period:'One-time payment', 
      billingType: 'payment'
    }
  },
  threeMonth: {
    inr: {
      amount: 225000,
      formatted: '₹2,250',
      period: 'One-time payment',
      billingType: 'payment'
    },
    usd: {
      amount: 2900,
      formatted: '$29.00',
      period: 'One-time payment',
      billingType: 'payment'
    }
  },
  sixMonth: {
    inr: {
      amount: 365000,
      formatted: '₹3,650',
      period: 'Every 6 months',
      billingType: 'recurring'
    },
    usd: {
      amount: 4500,
      formatted: '$45.00',
      period: 'Every 6 months',
      billingType: 'recurring'
    }
  },
  yearly: {
    inr: {
      amount: 579900,
      formatted: '₹5,799',
      period: 'One-time payment',
      billingType: 'payment'
    },
    usd: {
      amount: 7900,
      formatted: '$79.00',
      period: 'One-time payment',
      billingType: 'payment'
    }
  },
  lifetime: {
    inr: {
      amount: 749900,
      formatted: '₹7,499',
      period: 'Lifetime access',
      billingType: 'payment'
    },
    usd: {
      amount: 7900,
      formatted: '$79.00',
      period: 'Lifetime access',
      billingType: 'payment'
    }
  }
};

/**
 * Get the brand configuration
 * 
 * @returns {Object} Brand configuration
 */
export const getBrandConfig = () => {
  return BRAND_CONFIG;
};

/**
 * Get current domain name for backend communication
 * 
 * @returns {string} Current domain name
 */
export const getCurrentDomainName = () => {
  return 'maxresumes.com';
};

/**
 * Detect user's country using IP geolocation and timezone
 * 
 * @returns {Promise<string>} Two-letter country code (e.g., 'IN', 'US')
 */
export const detectUserCountry = async () => {
  try {
    // Try to get from localStorage first (to avoid multiple API calls)
    const cacheKey = 'userCountry_maxresumes';
    const cachedCountry = localStorage.getItem(cacheKey);
    if (cachedCountry) {
      return cachedCountry;
    }
    
    // Instant, zero-latency timezone detection (never rate-limited, never blocked)
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      if (timeZone === 'Asia/Kolkata' || timeZone === 'Asia/Calcutta') {
        localStorage.setItem(cacheKey, 'IN');
        return 'IN';
      }
    } catch (tzErr) {
      console.warn('Timezone detection skipped:', tzErr);
    }

    // Use a fast geolocation fetch with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2500);

    try {
      const response = await fetch('https://ipapi.co/json/', { signal: controller.signal });
      clearTimeout(timeoutId);
      if (response.ok) {
        const data = await response.json();
        if (data && data.country_code) {
          localStorage.setItem(cacheKey, data.country_code);
          return data.country_code;
        }
      }
    } catch (fetchErr) {
      // Ignore API errors, fall through to US
    }
    
    // Default to 'US' for all other global visitors
    return 'US';
  } catch (error) {
    console.error('Error detecting user country:', error);
    return 'US';
  }
};

/**
 * Get the currency based on the user's country
 * 
 * @param {string} countryCode - Two-letter country code
 * @returns {string} Currency code ('inr' or 'usd')
 */
export const getCurrencyForCountry = (countryCode) => {
  return countryCode === 'IN' ? 'inr' : 'usd';
};

/**
 * Get the payment processor based on currency
 * 
 * @param {string} currency - Currency code (inr, usd)
 * @returns {string} Payment processor name ('razorpay' or 'stripe')
 */
export const getPaymentProcessorForCurrency = (currency) => {
  const currencyLower = currency.toLowerCase();
  
  // Use Razorpay for INR, Stripe for everything else
  return currencyLower === 'inr' ? 'razorpay' : 'stripe';
};

/**
 * Get the price configuration for a plan with the specified currency
 * 
 * @param {string} planId - Plan ID (daily, oneMonth, threeMonth, sixMonth, yearly)
 * @param {string} currency - Currency code (inr, usd)
 * @returns {Object} Price configuration
 */
export const getPriceConfig = (planId, currency) => {
  // Check if the plan exists, if not, return a default
  if (!PRICE_CONFIG[planId]) {
    console.error(`Plan ${planId} not found in PRICE_CONFIG`);
    return PRICE_CONFIG.oneMonth.usd; // Return a default
  }
  
  return PRICE_CONFIG[planId] && PRICE_CONFIG[planId][currency.toLowerCase()]
    ? PRICE_CONFIG[planId][currency.toLowerCase()]
    : PRICE_CONFIG[planId]['usd']; // Default to USD if the currency is not supported
};

/**
 * Get the billing type for a plan with the specified currency
 * 
 * @param {string} planId - Plan ID (daily, oneMonth, threeMonth, sixMonth, yearly)
 * @param {string} currency - Currency code (inr, usd)
 * @returns {string} Billing type ('payment' or 'recurring')
 */
export const getPlanBillingType = (planId, currency) => {
  const planConfig = getPriceConfig(planId, currency.toLowerCase());
  return planConfig.billingType || 'recurring';
};

/**
 * Check if a plan auto-renews based on its billing type
 * 
 * @param {string} planId - Plan ID (daily, oneMonth, threeMonth, sixMonth, yearly)
 * @param {string} currency - Currency code (inr, usd)
 * @returns {boolean} True if the plan auto-renews
 */
export const doesPlanAutoRenew = (planId, currency) => {
  return getPlanBillingType(planId, currency) === 'recurring';
};

/**
 * Format currency amount with proper formatting
 * 
 * @param {number} amount - Amount in cents or paise
 * @param {string} currency - Currency code (default: 'usd')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'usd') => {
  if (amount === undefined || amount === null) return 'N/A';
  
  const currencyCode = currency.toUpperCase();
  const locale = currencyCode === 'INR' ? 'en-IN' : 'en-US';
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2
  }).format(amount / 100);
};

/**
 * Format a date string or timestamp
 * 
 * @param {Date|number|string} date - Date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return 'N/A';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };
  
  const dateObj = date instanceof Date 
    ? date 
    : typeof date === 'number' 
      ? new Date(date * 1000) 
      : new Date(date);
  
  return dateObj.toLocaleDateString('en-US', { ...defaultOptions, ...options });
};

/**
 * Format time remaining for daily subscription
 * 
 * @param {Date} expiryDate - The expiry date of the subscription
 * @returns {string} Formatted time remaining string
 */
export const formatTimeRemaining = (expiryDate) => {
  if (!expiryDate) return 'Expired';
  
  const now = new Date();
  const expiry = expiryDate instanceof Date ? expiryDate : new Date(expiryDate);
  
  // If expired, return expired message
  if (now > expiry) return 'Expired';
  
  // Calculate time difference in milliseconds
  const timeDiff = expiry - now;
  
  // Convert to hours and minutes
  const hours = Math.floor(timeDiff / (1000 * 60 * 60));
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 0) {
    return `${hours}h ${minutes}m remaining`;
  } else {
    return `${minutes}m remaining`;
  }
};

/**
 * Get number of months for a plan type
 * 
 * @param {string} planId - Plan ID (oneMonth, threeMonth, etc.)
 * @returns {number} Number of months
 */
export const getMonthsForPlan = (planId) => {
  switch (planId) {
    case 'oneMonth': return 1;
    case 'threeMonth': return 3;
    case 'sixMonth': return 6;
    case 'yearly': return 12;
    default: return 1;
  }
};

/**
 * Get CSS variables object for brand colors
 * 
 * @returns {Object} CSS variables object
 */
export const getBrandCssVars = () => {
  return {
    '--primary-color': BRAND_CONFIG.primaryColor,
    '--secondary-color': BRAND_CONFIG.secondaryColor,
    '--brand-name': `"${BRAND_CONFIG.name}"`,
  };
};

export default {
  getBrandConfig,
  getCurrentDomainName,
  detectUserCountry,
  getCurrencyForCountry,
  getPaymentProcessorForCurrency,
  getPriceConfig,
  getPlanBillingType,
  doesPlanAutoRenew,
  formatCurrency,
  formatDate,
  formatTimeRemaining,
  getMonthsForPlan,
  getBrandCssVars
};